import { Autocomplete, Box, Button, CircularProgress, Grid, Input, Snackbar, Typography } from "@mui/joy";
import React from "react";
import { Building } from "./BuildingList";
import { useRole } from "../Contexts/RoleContext";

/**
 * Interface for the ad data structure
 */
interface AdData {
	leftImage?: string;
	rightImage?: string;
	bottomImage?: string;
	scrollingText?: string;
}

/**
 * ElevatorAds component - Editor for creating and managing elevator advertisements
 */
export default function ElevatorAds() {
	const [adData, setAdData] = React.useState<AdData>({});
	const [loading, setLoading] = React.useState(false);
	const [uploading, setUploading] = React.useState<string | null>(null);
	const [buildings, setBuildings] = React.useState([] as Array<Building>);
	const [selectedBuilding, setSelectedBuilding] = React.useState<Building | null>(null);
	const [saveSuccess, setSaveSuccess] = React.useState(false);
	const [saveError, setSaveError] = React.useState<string | null>(null);
	const { effectiveRole } = useRole();

	// Fetch buildings on component mount
	React.useEffect(() => getBuildings(), [effectiveRole]);

	/**
	 * Fetches the list of buildings from the API
	 */
	const getBuildings = () => {
		fetch(effectiveRole === "Admin" ? "/Building/GetBuildingsList" : "/Building/GetClientPortalBuildings", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ filter: true }),
		})
			.then((x) => x.json())
			.then((x) => {
				const bs = x
					.sort((a: any, b: any) => a.building_name.localeCompare(b.building_name))
					.map((y: any) => {
						return { Name: y.building_name, ID: y.buildings_id } as Building;
					});
				setBuildings(bs);
			});
	};

	/**
	 * Fetches existing elevator ad data for the selected building
	 */
	const getElevatorAds = () => {
		setLoading(true);
		fetch("/Building/GetElevatorAd", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(selectedBuilding?.ID),
		})
			.then((x) => x.text())
			.then((x) => {
				if (x === "") {
					setAdData({});
				} else {
					setAdData(JSON.parse(x));
				}
			})
			.catch((err) => {
				console.error("Error loading ad data:", err);
				setAdData({});
			})
			.finally(() => setLoading(false));
	};

	// Load ad data when building is selected
	React.useEffect(() => {
		if (selectedBuilding) {
			getElevatorAds();
		}
	}, [selectedBuilding]);

	/**
	 * Handles file upload to Azure Blob Storage
	 */
	const handleFileUpload = async (file: File, imageType: 'leftImage' | 'rightImage' | 'bottomImage') => {
		setUploading(imageType);
		const formData = new FormData();
		formData.append('file', file);

		try {
			const response = await fetch('/Building/UploadAdImage', {
				method: 'POST',
				body: formData,
			});

			const result = await response.json();
			if (result.url) {
				setAdData(prev => ({ ...prev, [imageType]: result.url }));
			} else if (result.error) {
				setSaveError(`Upload failed: ${result.error}`);
			}
		} catch (error) {
			console.error('Upload error:', error);
			setSaveError('Failed to upload image');
		} finally {
			setUploading(null);
		}
	};

	/**
	 * Handles drag and drop events for image upload
	 */
	const handleDrop = (e: React.DragEvent, imageType: 'leftImage' | 'rightImage' | 'bottomImage') => {
		e.preventDefault();
		e.stopPropagation();
		
		const file = e.dataTransfer.files[0];
		if (file && file.type.startsWith('image/')) {
			handleFileUpload(file, imageType);
		}
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	};

	/**
	 * Handles file selection via file input
	 */
	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, imageType: 'leftImage' | 'rightImage' | 'bottomImage') => {
		const file = e.target.files?.[0];
		if (file) {
			handleFileUpload(file, imageType);
		}
	};

	/**
	 * Saves the ad data to the database
	 */
	const saveAdData = async () => {
		if (!selectedBuilding) {
			setSaveError('Please select a building first');
			return;
		}

		setLoading(true);
		try {
			await fetch('/Building/SaveElevatorAd', {
				method: 'POST',
				cache: 'no-cache',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					buildingId: selectedBuilding.ID,
					adData: adData,
				}),
			});

			setSaveSuccess(true);
		} catch (error) {
			console.error('Save error:', error);
			setSaveError('Failed to save ad data');
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Renders an image upload box with drag-and-drop support
	 */
	const renderImageBox = (
		imageType: 'leftImage' | 'rightImage' | 'bottomImage',
		label: string,
		height: string = "100%"
	) => {
		const imageUrl = adData[imageType];
		const isUploading = uploading === imageType;

		return (
			<Box
				onDrop={(e) => handleDrop(e, imageType)}
				onDragOver={handleDragOver}
				sx={{
					border: imageUrl ? "none" : "4px dashed #ebebeb",
					borderRadius: "sm",
					height: height,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					backgroundColor: imageUrl ? "transparent" : "#fafafa",
					position: "relative",
					cursor: "pointer",
					"&:hover": {
						backgroundColor: imageUrl ? "transparent" : "#f0f0f0",
					},
				}}
			>
				{isUploading ? (
					<CircularProgress />
				) : imageUrl ? (
					<>
						<img
							src={imageUrl}
							alt={label}
							style={{
								maxWidth: "100%",
								maxHeight: "100%",
								width: "auto",
								height: "auto",
								objectFit: "contain",
								borderRadius: "4px",
							}}
						/>
						<Button
							size="sm"
							color="danger"
							sx={{
								position: "absolute",
								top: 8,
								right: 8,
							}}
							onClick={() => setAdData(prev => ({ ...prev, [imageType]: undefined }))}
						>
							Remove
						</Button>
					</>
				) : (
					<Box sx={{ textAlign: "center", padding: 2 }}>
						<Typography level="body-sm" sx={{ color: "#999", marginBottom: 1 }}>
							{label}
						</Typography>
						<Typography level="body-xs" sx={{ color: "#999", marginBottom: 2 }}>
							Drag & drop or click to upload
						</Typography>
						<input
							type="file"
							accept="image/*"
							style={{ display: "none" }}
							id={`upload-${imageType}`}
							onChange={(e) => handleFileSelect(e, imageType)}
						/>
						<label htmlFor={`upload-${imageType}`}>
							<Button component="span" size="sm">
								Choose File
							</Button>
						</label>
					</Box>
				)}
			</Box>
		);
	};

	return (
		<>
			<Box sx={{ overflowY: "auto", padding: 2, display: "flex", flexDirection: "column", flex: 1 }}>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
					<Typography level="title-lg">
						Elevator Ads Editor
					</Typography>
					<Box sx={{ display: "flex", gap: 2 }}>
						<Button
							onClick={() => {
								if (selectedBuilding) {
									alert("Any unsaved changes won't be displayed - save before previewing.")
									window.open(`https://portal.dynamicelevator.com/adrender?id=${selectedBuilding.ID}`, '_blank');
								}
							}}
							disabled={!selectedBuilding}
							color="neutral"
							variant="outlined"
						>
							Preview
						</Button>
						<Button
							onClick={saveAdData}
							disabled={!selectedBuilding || loading}
							loading={loading}
							color="primary"
						>
							Save Changes
						</Button>
					</Box>
				</Box>

				<Autocomplete
					size="sm"
					sx={{ maxWidth: "300px", marginBottom: 3 }}
					options={buildings}
					getOptionLabel={(option) => option.Name}
					onChange={(event, value) => { setSelectedBuilding(value) }}
					value={selectedBuilding}
					placeholder="Select a building..."
				/>

				{!selectedBuilding ? (
					<Box sx={{ textAlign: "center", padding: 4 }}>
						<Typography level="body-md" sx={{ color: "#999" }}>
							Please select a building to edit its elevator ads
						</Typography>
					</Box>
				) : (
					<>
						<Box sx={{ display: "flex", flexDirection: "row", flex: 1 }}>
							<Grid container spacing={4} sx={{ flex: 1, flexDirection: "column" }}>
								{/* Two side-by-side image boxes */}
								<Grid xs={12} sx={{ display: "flex", flex: 1 }}>
									<Grid container spacing={2} xs={12} sx={{ flex: 3 }}>
										<Grid xs={6} sx={{ flex: 1 }}>
											{renderImageBox('leftImage', 'Left Image')}
										</Grid>
										<Grid xs={6} sx={{ flex: 1 }}>
											{renderImageBox('rightImage', 'Right Image')}
										</Grid>
									</Grid>
								</Grid>

								{/* Bottom banner image */}
								<Grid container spacing={2} xs={12} sx={{ flex: 1, maxHeight: "30%" }}>
									<Grid xs={12} sx={{ flex: 1 }}>
										{renderImageBox('bottomImage', 'Bottom Banner Image')}
									</Grid>
								</Grid>

								{/* Scrolling text input and preview */}
								<Grid container spacing={2} xs={12} sx={{ flex: "none" }}>
									<Grid xs={12}>
										<Typography level="body-sm" sx={{ marginBottom: 1, fontWeight: "bold" }}>
											Scrolling Text
										</Typography>
										<Input
											placeholder="Enter scrolling text message..."
											value={adData.scrollingText || ""}
											onChange={(e) => setAdData(prev => ({ ...prev, scrollingText: e.target.value }))}
											sx={{ marginBottom: 2 }}
										/>
									</Grid>
								</Grid>

								{/* Scrolling text preview */}
								<Grid container spacing={2} xs={12} sx={{ flex: 1, maxHeight: "60px", overflowX: "hidden" }}>
									<Grid
										sx={{
											overflow: 'hidden',
											whiteSpace: 'nowrap',
											position: 'relative',
											border: "2px solid #ebebeb",
											borderRadius: "sm",
											backgroundColor: "#fafafa",
											'@keyframes scrollText': {
												'0%': { transform: 'translateX(100%)' },
												'100%': { transform: 'translateX(-100%)' }
											}
										}}
										container
										xs={12}
									>
										<Typography
											level="body-lg"
											sx={{
												animation: 'scrollText 30s linear infinite',
												display: 'inline-block',
												whiteSpace: 'nowrap',
												minWidth: '100%',
												fontSize: '2em',
												padding: 1
											}}
										>
											{adData.scrollingText || "Your scrolling text will appear here..."}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Box>
					</>
				)}
			</Box>

			{/* Success notification */}
			<Snackbar
				open={saveSuccess}
				autoHideDuration={3000}
				onClose={() => setSaveSuccess(false)}
				color="success"
			>
				Ad data saved successfully!
			</Snackbar>

			{/* Error notification */}
			<Snackbar
				open={!!saveError}
				autoHideDuration={5000}
				onClose={() => setSaveError(null)}
				color="danger"
			>
				{saveError}
			</Snackbar>
		</>
	);
}
